import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberGreaterThanOrEqualToZero, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { addRecordToBottom, create, createBlind, createWithId, getAll, getByDoubleQuery, getById, getByIdQuery, getBySingleQuery, getReference, remove, update, updateField, updateFields } from '../utils/dbUtils';
import { collection, doc, endBefore, getCountFromServer, getDoc, getDocs, increment, limit, limitToLast, orderBy, query, startAfter, where, writeBatch } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { toLower } from "lodash";
import { asTimeZone, combineDateAndTimeAsTimeZoneUtcDate, formatDueAt, fromUtcToDate, fromUtcToTimeZone, fromUtcToTimeZoneAsTimeZone } from "../utils/dateFormatter";
import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import StatusHistory from "./StatusHistory";
import TaskNote from "./TaskNote";
import TaskReminder from "./TaskReminder";
import User from "./User";
import Customer from "./Customer";
import { buildTaskSlaMetricFields } from "../utils/slaMetricFields";

// JustCall Sync Rules:
// PENDING or IN_PROGRESS + isSynced=true → Task is currently in the JustCall campaign
// PENDING or IN_PROGRESS + isSynced=false + isSuppressed=false → Should be synced to JustCall
// PENDING or IN_PROGRESS + isSuppressed=true → Should NOT be in JustCall (ReadyRx only, can be un-suppressed)
// COMPLETED or CANCELLED → Terminal statuses, should NOT be in JustCall (remove if synced)
// task.category maps to the JustCall campaign ID

class Task {
    static collectionName = 'tasks';

    title = '';
    description = null; // optional detailed description of the task
    userId = null; // userId of the patient this task is related to
    assignedTo = 'NONE'; // userId of the staff member assigned to this task
    assignedAt = null;
    status = 'PENDING'; // 'PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'
    statusHistory = []; // list of StatusHistory, historical changes to status
    priority = 'MEDIUM'; // 'LOW', 'MEDIUM', 'HIGH', 'URGENT'
    category = 'NONE'; // 'NONE', 'GENERAL', 'FOLLOW_UP', 'MEDICATION', 'TEST', 'ENCOUNTER', 'SUBSCRIPTION', 'RENEWAL', 'CANCELLATION', 'CHARGE_BACK'.
    subCategory = 'NONE'; // secondary category, same enum as category
    activityType = 'NONE'; // EMAIL, SMS, CALL, FACE, NONE

    // Ticket-specific fields (only used when type = 'TICKET')
    type = 'TASK'; // 'TASK' | 'TICKET'
    ticketNumber = null; // Auto-incrementing human-readable ticket # (e.g., "TKT-00421")
    channel = null; // 'CHAT' | 'EMAIL' | 'SMS' | 'PHONE' | 'INTERNAL' | 'SYSTEM'
    sourceConversationId = null; // The support conversation that originated this ticket
    caseKey = null; // Stable thread-level key tying tickets to channel conversations over time
    sla = {
        responseDeadline: null,
        resolutionDeadline: null,
        firstResponseAt: null,
        isResponseBreached: false,
        isResolutionBreached: false
    };
    satisfaction = {
        rating: null,
        feedback: null,
        ratedAt: null
    };
    escalationLevel = 0; // 0 = normal, 1 = escalated, 2 = critical
    escalatedAt = null;
    escalatedBy = null;
    escalationReason = null;
    tags = [];
    watcherUserIds = [];
    ccEmails = [];
    routing = {
        strategy: null,
        reason: null,
        score: null,
        matchedCategories: [],
        lastEvaluatedAt: null
    };
    aiHandled = false;
    aiSummary = null;
    mergedIntoTicketId = null;
    mergedTicketIds = [];
    lastActivityAt = null;
    lastCustomerReplyAt = null;
    lastStaffReplyAt = null;
    lastMessagePreview = null;
    customerResponseRequired = false;
    customerResponseRequiredAt = null;
    customerResponseRequiredSourceId = null;

    // Timing and deadlines
    dueAt = {
        timestamp: null,
        date: null,
        time: null
    };
    completedAt = null;
    estimatedDuration = null; // in minutes

    // Relationships
    relatedConversationId = null; // ID of the related chat session or conversation
    parentTaskId = null;
    childTaskIds = [];
    relatedTaskIds = [];

    // Reminders
    reminderIds = [];
    remindersCount = 0;

    // Task completion and follow-up
    notes = [];
    notesCount = 0;
    followUpRequired = false;
    followUpInstructions = null;

    // Additional metadata
    color = '#ffffff';
    fileAttachments = []; // Array of { url, refId, type, name, size, metadata }
    metadata = {
        createdReason: null,
        suppressedReason: null,
        completedReason: null,
        cancelledReason: null
    };
    contextData = {};

    customer = new Customer(null).toDto();
    entityId = null;
    entityType = null;
    isSynced = false;
    syncedAt = null;
    isSuppressed = false;
    suppressedAt = null;
    slaOwnerId = 'NONE';
    slaChannel = null;
    slaResponded = false;
    slaResponseAt = null;
    slaResponseMinutes = null;
    slaResponseTargetMinutes = null;
    slaResponseBreached = null;
    slaResponseWithinTarget = null;
    slaResolved = false;
    slaResolutionAt = null;
    slaResolutionMinutes = null;
    slaResolutionTargetMinutes = null;
    slaResolutionBreached = null;
    slaResolutionWithinTarget = null;
    slaMetricVersion = null;

    constructor(task) {
        if (isObjectNotEmpty(task)) {
            if (isStringNotEmpty(task.title)) { this.title = task.title; }
            if (isStringNotEmpty(task.description)) { this.description = task.description; }
            if (isStringNotEmpty(task.userId)) { this.userId = task.userId; }
            if (isStringNotEmpty(task.assignedTo)) { this.assignedTo = task.assignedTo; }
            if (isNotNullOrUndefined(task.assignedAt)) { this.assignedAt = task.assignedAt; }
            if (isStringNotEmpty(task.status)) { this.status = task.status; }
            if (isListNotEmpty(task.statusHistory)) { this.statusHistory = task.statusHistory; }
            else if (isStringNotEmpty(this.status)) { this.statusHistory = StatusHistory.addNewStatus(this.status); }

            if (isStringNotEmpty(task.priority)) { this.priority = task.priority; }
            if (isStringNotEmpty(task.category)) { this.category = task.category; }
            if (isStringNotEmpty(task.subCategory)) { this.subCategory = task.subCategory; }
            if (isStringNotEmpty(task.activityType)) { this.activityType = task.activityType; }

            // Ticket-specific fields
            if (isStringNotEmpty(task.type)) { this.type = task.type; }
            if (isStringNotEmpty(task.ticketNumber)) { this.ticketNumber = task.ticketNumber; }
            if (isStringNotEmpty(task.channel)) { this.channel = task.channel; }
            if (isStringNotEmpty(task.sourceConversationId)) { this.sourceConversationId = task.sourceConversationId; }
            if (isStringNotEmpty(task.caseKey)) { this.caseKey = task.caseKey; }
            if (isObjectNotEmpty(task.sla)) {
                this.sla = {
                    responseDeadline: task.sla.responseDeadline || null,
                    resolutionDeadline: task.sla.resolutionDeadline || null,
                    firstResponseAt: task.sla.firstResponseAt || null,
                    isResponseBreached: task.sla.isResponseBreached || false,
                    isResolutionBreached: task.sla.isResolutionBreached || false
                };
            }
            if (isObjectNotEmpty(task.satisfaction)) {
                this.satisfaction = {
                    rating: task.satisfaction.rating || null,
                    feedback: task.satisfaction.feedback || null,
                    ratedAt: task.satisfaction.ratedAt || null
                };
            }
            if (isNumberGreaterThanOrEqualToZero(task.escalationLevel)) { this.escalationLevel = task.escalationLevel; }
            if (isNotNullOrUndefined(task.escalatedAt)) { this.escalatedAt = task.escalatedAt; }
            if (isStringNotEmpty(task.escalatedBy)) { this.escalatedBy = task.escalatedBy; }
            if (isStringNotEmpty(task.escalationReason)) { this.escalationReason = task.escalationReason; }
            if (isListNotEmpty(task.tags)) { this.tags = task.tags; }
            if (isListNotEmpty(task.watcherUserIds)) { this.watcherUserIds = task.watcherUserIds; }
            if (isListNotEmpty(task.ccEmails)) { this.ccEmails = task.ccEmails; }
            if (isObjectNotEmpty(task.routing)) {
                this.routing = {
                    strategy: task.routing.strategy || null,
                    reason: task.routing.reason || null,
                    score: task.routing.score ?? null,
                    matchedCategories: Array.isArray(task.routing.matchedCategories) ? task.routing.matchedCategories : [],
                    lastEvaluatedAt: task.routing.lastEvaluatedAt || null
                };
            }
            if (isBooleanTrue(task.aiHandled)) { this.aiHandled = true; }
            if (isStringNotEmpty(task.aiSummary)) { this.aiSummary = task.aiSummary; }
            if (isStringNotEmpty(task.mergedIntoTicketId)) { this.mergedIntoTicketId = task.mergedIntoTicketId; }
            if (isListNotEmpty(task.mergedTicketIds)) { this.mergedTicketIds = task.mergedTicketIds; }
            if (isNotNullOrUndefined(task.lastActivityAt)) { this.lastActivityAt = task.lastActivityAt; }
            if (isNotNullOrUndefined(task.lastCustomerReplyAt)) { this.lastCustomerReplyAt = task.lastCustomerReplyAt; }
            if (isNotNullOrUndefined(task.lastStaffReplyAt)) { this.lastStaffReplyAt = task.lastStaffReplyAt; }
            if (isStringNotEmpty(task.lastMessagePreview)) { this.lastMessagePreview = task.lastMessagePreview; }
            if (isBooleanTrue(task.customerResponseRequired)) { this.customerResponseRequired = true; }
            if (isBooleanFalse(task.customerResponseRequired)) { this.customerResponseRequired = false; }
            if (isNotNullOrUndefined(task.customerResponseRequiredAt)) { this.customerResponseRequiredAt = task.customerResponseRequiredAt; }
            if (isStringNotEmpty(task.customerResponseRequiredSourceId)) { this.customerResponseRequiredSourceId = task.customerResponseRequiredSourceId; }

            if (isObjectNotEmpty(task.dueAt)) {
                if (task.dueAt instanceof Date) {
                    // Handle legacy Date objects
                    const m = moment(task.dueAt);
                    this.dueAt = {
                        timestamp: task.dueAt,
                        date: m.format('YYYY-MM-DD'),
                        time: m.format('HH:mm')
                    };
                } else {
                    this.dueAt = task.dueAt;
                }
            } else if (isNotNullOrUndefined(task.dueAt)) {
                const m = moment(task.dueAt);
                this.dueAt = {
                    timestamp: m.toDate(),
                    date: m.format('YYYY-MM-DD'),
                    time: m.format('HH:mm')
                };
            }
            if (isNotNullOrUndefined(task.completedAt)) { this.completedAt = task.completedAt; }
            if (isNumberGreaterThanOrEqualToZero(task.estimatedDuration)) { this.estimatedDuration = task.estimatedDuration; }

            if (isStringNotEmpty(task.relatedConversationId)) { this.relatedConversationId = task.relatedConversationId; }
            if (isStringNotEmpty(task.parentTaskId)) { this.parentTaskId = task.parentTaskId; }
            if (isListNotEmpty(task.childTaskIds)) { this.childTaskIds = task.childTaskIds; }
            if (isListNotEmpty(task.relatedTaskIds)) { this.relatedTaskIds = task.relatedTaskIds; }

            if (isListNotEmpty(task.reminderIds)) { this.reminderIds = task.reminderIds; }
            if (isNumberGreaterThanOrEqualToZero(task.remindersCount)) { this.remindersCount = task.remindersCount; }

            if (isListNotEmpty(task.notes)) { this.notes = task.notes; }
            if (isNumberGreaterThanOrEqualToZero(task.notesCount)) { this.notesCount = task.notesCount; }
            if (isBooleanTrue(task.followUpRequired)) { this.followUpRequired = true; }
            if (isStringNotEmpty(task.followUpInstructions)) { this.followUpInstructions = task.followUpInstructions; }

            if (isStringNotEmpty(task.color)) { this.color = task.color; }
            if (isListNotEmpty(task.fileAttachments)) { this.fileAttachments = task.fileAttachments; }
            if (isObjectNotEmpty(task.metadata)) {
                this.metadata = {
                    ...task.metadata,
                    createdReason: task.metadata.createdReason || null,
                    suppressedReason: task.metadata.suppressedReason || null,
                    completedReason: task.metadata.completedReason || null,
                    cancelledReason: task.metadata.cancelledReason || null
                };
            }
            if (isObjectNotEmpty(task.contextData)) { this.contextData = task.contextData; }

            if (isObjectNotEmpty(task.customer)) { this.customer = new Customer(task.customer).toDto(); }
            if (isStringNotEmpty(task.entityId)) { this.entityId = task.entityId; }
            if (isStringNotEmpty(task.entityType)) { this.entityType = task.entityType; }
            if (isBooleanTrue(task.isSynced)) { this.isSynced = true; }
            if (isNotNullOrUndefined(task.syncedAt)) { this.syncedAt = task.syncedAt; }
            if (isBooleanTrue(task.isSuppressed)) { this.isSuppressed = true; }
            if (isNotNullOrUndefined(task.suppressedAt)) { this.suppressedAt = task.suppressedAt; }
            if (isStringNotEmpty(task.slaOwnerId)) { this.slaOwnerId = task.slaOwnerId; }
            if (isStringNotEmpty(task.slaChannel)) { this.slaChannel = task.slaChannel; }
            if (isBooleanTrue(task.slaResponded)) { this.slaResponded = true; }
            if (isBooleanFalse(task.slaResponded)) { this.slaResponded = false; }
            if (isNotNullOrUndefined(task.slaResponseAt)) { this.slaResponseAt = task.slaResponseAt; }
            if (isNotNullOrUndefined(task.slaResponseMinutes)) { this.slaResponseMinutes = task.slaResponseMinutes; }
            if (isNotNullOrUndefined(task.slaResponseTargetMinutes)) { this.slaResponseTargetMinutes = task.slaResponseTargetMinutes; }
            if (isBooleanTrue(task.slaResponseBreached)) { this.slaResponseBreached = true; }
            if (isBooleanFalse(task.slaResponseBreached)) { this.slaResponseBreached = false; }
            if (isBooleanTrue(task.slaResponseWithinTarget)) { this.slaResponseWithinTarget = true; }
            if (isBooleanFalse(task.slaResponseWithinTarget)) { this.slaResponseWithinTarget = false; }
            if (isBooleanTrue(task.slaResolved)) { this.slaResolved = true; }
            if (isBooleanFalse(task.slaResolved)) { this.slaResolved = false; }
            if (isNotNullOrUndefined(task.slaResolutionAt)) { this.slaResolutionAt = task.slaResolutionAt; }
            if (isNotNullOrUndefined(task.slaResolutionMinutes)) { this.slaResolutionMinutes = task.slaResolutionMinutes; }
            if (isNotNullOrUndefined(task.slaResolutionTargetMinutes)) { this.slaResolutionTargetMinutes = task.slaResolutionTargetMinutes; }
            if (isBooleanTrue(task.slaResolutionBreached)) { this.slaResolutionBreached = true; }
            if (isBooleanFalse(task.slaResolutionBreached)) { this.slaResolutionBreached = false; }
            if (isBooleanTrue(task.slaResolutionWithinTarget)) { this.slaResolutionWithinTarget = true; }
            if (isBooleanFalse(task.slaResolutionWithinTarget)) { this.slaResolutionWithinTarget = false; }
            if (isNotNullOrUndefined(task.slaMetricVersion)) { this.slaMetricVersion = task.slaMetricVersion; }
        }
    }

    toDto = () => {
        return {
            title: this.title,
            description: this.description,
            userId: this.userId,
            assignedTo: this.assignedTo,
            assignedAt: this.assignedAt,
            status: this.status,
            statusHistory: this.statusHistory,
            priority: this.priority,
            category: this.category,
            subCategory: this.subCategory,
            activityType: this.activityType,

            type: this.type,
            ticketNumber: this.ticketNumber,
            channel: this.channel,
            sourceConversationId: this.sourceConversationId,
            caseKey: this.caseKey,
            sla: this.sla,
            satisfaction: this.satisfaction,
            escalationLevel: this.escalationLevel,
            escalatedAt: this.escalatedAt,
            escalatedBy: this.escalatedBy,
            escalationReason: this.escalationReason,
            tags: this.tags,
            watcherUserIds: this.watcherUserIds,
            ccEmails: this.ccEmails,
            routing: this.routing,
            aiHandled: this.aiHandled,
            aiSummary: this.aiSummary,
            mergedIntoTicketId: this.mergedIntoTicketId,
            mergedTicketIds: this.mergedTicketIds,
            lastActivityAt: this.lastActivityAt,
            lastCustomerReplyAt: this.lastCustomerReplyAt,
            lastStaffReplyAt: this.lastStaffReplyAt,
            lastMessagePreview: this.lastMessagePreview,
            customerResponseRequired: this.customerResponseRequired,
            customerResponseRequiredAt: this.customerResponseRequiredAt,
            customerResponseRequiredSourceId: this.customerResponseRequiredSourceId,

            dueAt: this.dueAt,
            completedAt: this.completedAt,
            estimatedDuration: this.estimatedDuration,

            relatedConversationId: this.relatedConversationId,
            parentTaskId: this.parentTaskId,
            childTaskIds: this.childTaskIds,
            relatedTaskIds: this.relatedTaskIds,

            reminderIds: this.reminderIds,
            remindersCount: this.remindersCount,

            notes: this.notes,
            notesCount: this.notesCount,
            followUpRequired: this.followUpRequired,
            followUpInstructions: this.followUpInstructions,

            color: this.color,
            fileAttachments: this.fileAttachments,
            metadata: this.metadata,
            contextData: this.contextData,

            customer: this.customer,
            entityId: this.entityId,
            entityType: this.entityType,
            isSynced: this.isSynced,
            syncedAt: this.syncedAt,
            isSuppressed: this.isSuppressed,
            suppressedAt: this.suppressedAt,
            slaOwnerId: this.slaOwnerId,
            slaChannel: this.slaChannel,
            slaResponded: this.slaResponded,
            slaResponseAt: this.slaResponseAt,
            slaResponseMinutes: this.slaResponseMinutes,
            slaResponseTargetMinutes: this.slaResponseTargetMinutes,
            slaResponseBreached: this.slaResponseBreached,
            slaResponseWithinTarget: this.slaResponseWithinTarget,
            slaResolved: this.slaResolved,
            slaResolutionAt: this.slaResolutionAt,
            slaResolutionMinutes: this.slaResolutionMinutes,
            slaResolutionTargetMinutes: this.slaResolutionTargetMinutes,
            slaResolutionBreached: this.slaResolutionBreached,
            slaResolutionWithinTarget: this.slaResolutionWithinTarget,
            slaMetricVersion: this.slaMetricVersion
        };
    }

    static formatDueAt = (date, time = null) => {
        const m = moment(date);
        if (time) {
            const [hours, minutes] = time.split(':');
            m.hours(parseInt(hours));
            m.minutes(parseInt(minutes));
            m.seconds(0);
        } else {
            m.hours(0);
            m.minutes(0);
            m.seconds(0)
        }
        console.log(date, time);
        const formattedDateTimeString = `${date}T${(time || '00:00') + ':00'}`;
        const localMoment = moment.tz(formattedDateTimeString, 'US/Pacific');
        const timestamp = moment.tz(formattedDateTimeString, 'US/Pacific').utc().toDate();

        return {
            timestamp: timestamp,
            date: localMoment.format('YYYY-MM-DD'),
            time: time
        };
    }

    isValid = () => {
        if (isStringEmpty(this.title)) {
            throw new Error('Task title is required.');
        }
        if (isStringEmpty(this.userId)) {
            throw new Error('Patient UserId is required.');
        }
        return true;
    }

    create = async () => {
        if (this.isValid()) {
            // Get a new write batch
            const batch = writeBatch(db);

            // Create new task
            const taskId = uuid();
            const taskRef = doc(collection(db, Task.collectionName), taskId);

            let newTask = this.toDto();

            // Create reminder if dueAt time is set
            if (isStringNotEmpty(this.dueAt.time) && isStringNotEmpty(this.assignedTo) && this.assignedTo !== 'NONE') {
                const reminderId = uuid();
                const reminderRef = doc(collection(db, TaskReminder.collectionName), reminderId);

                const reminder = new TaskReminder({
                    taskId: taskId,
                    userId: this.assignedTo,
                    type: 'DEADLINE_WARNING',
                    message: `Task "${this.title}" is due on ${formatDueAt(this.dueAt)}.`,
                    reminderAt: moment(this.dueAt.timestamp).subtract(15, 'minutes').toDate(),
                    methods: ['PUSH']
                });

                let newReminder = reminder.toDto();
                newReminder.createdAt = moment().utc().toDate();
                newReminder.createdBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
                newReminder.modifiedAt = moment().utc().toDate();
                newReminder.modifiedBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
                newReminder.isDeleted = false;

                batch.set(reminderRef, newReminder);

                // Add reminderId to task
                newTask.reminderIds = [reminderId];
                newTask.remindersCount = 1;
            }

            newTask.createdAt = moment().utc().toDate();
            newTask.createdBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
            newTask.modifiedAt = moment().utc().toDate();
            newTask.modifiedBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
            newTask.isDeleted = false;
            Object.assign(newTask, buildTaskSlaMetricFields({ task: newTask }));

            batch.set(taskRef, newTask);

            // Increment user's task count
            const userRef = doc(collection(db, 'users'), this.userId);
            batch.update(userRef, {
                taskCount: increment(1)
            });

            // Commit the batch
            await batch.commit();

            return { ...newTask, id: taskId };
        }
    }

    static updateStatus = async (id, newStatus, note = '', oldObj) => {
        if (isStringNotEmpty(id)) {
            const batch = writeBatch(db);

            const now = moment().utc().toDate();
            const statusHistory = StatusHistory.addNewStatus(newStatus, oldObj?.statusHistory, now);
            let updatedFields = {
                status: newStatus,
                statusHistory,
                completedAt: newStatus === 'COMPLETED' ? now : null
            };
            if (['COMPLETED', 'CANCELLED'].includes(newStatus)) {
                updatedFields.customerResponseRequired = false;
                updatedFields.customerResponseRequiredAt = null;
                updatedFields.customerResponseRequiredSourceId = null;
            }
            updatedFields = {
                ...updatedFields,
                ...buildTaskSlaMetricFields({
                    task: {
                        ...oldObj,
                        ...updatedFields
                    }
                })
            };

            if (isStringNotEmpty(note)) {
                updatedFields.notes = addRecordToBottom('id', new TaskNote({ body: note, by: auth.currentUser.uid }).toDto(), oldObj?.notes);
                updatedFields.notesCount = isNumberGreaterThanOrEqualToZero(oldObj?.notesCount) ? oldObj.notesCount + 1 : 1;
            }

            // If status is changing to CANCELLED, decrement taskCount
            if (newStatus === 'CANCELLED' && oldObj?.status !== 'CANCELLED') {
                const userRef = doc(collection(db, 'users'), oldObj.userId);
                batch.update(userRef, {
                    taskCount: increment(-1)
                });

                // Delete unsent reminders
                if (isListNotEmpty(oldObj?.reminderIds)) {
                    // Get all reminders for this task
                    const remindersQuery = query(
                        collection(db, TaskReminder.collectionName),
                        where('taskId', '==', id),
                        where('isSent', '==', false)
                    );
                    const reminderSnapshot = await getDocs(remindersQuery);

                    // Delete each unsent reminder
                    const unsentReminderIds = [];
                    reminderSnapshot.forEach(doc => {
                        const reminderRef = doc.ref;
                        batch.delete(reminderRef);
                        unsentReminderIds.push(doc.id);
                    });

                    // Update task's reminderIds and count
                    if (unsentReminderIds.length > 0) {
                        const remainingReminderIds = oldObj.reminderIds.filter(id => !unsentReminderIds.includes(id));
                        updatedFields.reminderIds = remainingReminderIds;
                        updatedFields.remindersCount = remainingReminderIds.length;
                    }
                }
            }

            // If status is changing from CANCELLED to something else, increment taskCount
            if (oldObj?.status === 'CANCELLED' && newStatus !== 'CANCELLED') {
                const userRef = doc(collection(db, 'users'), oldObj.userId);
                batch.update(userRef, {
                    taskCount: increment(1)
                });
            }

            // Update task status
            const taskRef = doc(collection(db, Task.collectionName), id);
            batch.update(taskRef, updatedFields);


            await batch.commit();

            return { ...oldObj, ...updatedFields, id: id };
        }

        return null;
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Task.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(Task.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedTask, oldObj) => {
        const updatedObj = new Task(updatedTask);
        if (updatedObj.isValid() && id === updatedTask.id) {
            const success = await update(Task.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldObj, ...updatedObj.toDto() };
            }
        } else {
            throw new Error('id and id field of the updated task must match.')
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            const taskRef = doc(collection(db, Task.collectionName), id);
            const task = await getReference(taskRef);

            // Get a new write batch
            const batch = writeBatch(db);

            // Delete all associated reminders
            if (isListNotEmpty(task?.reminderIds)) {
                for (const reminderId of task?.reminderIds) {
                    const reminderRef = doc(collection(db, 'taskReminders'), reminderId);
                    batch.delete(reminderRef);
                }
            }

            // Decrement user's task count
            if (isObjectNotEmpty(task) && isStringNotEmpty(task.userId)) {
                const userRef = doc(collection(db, User.collectionName), task.userId);
                batch.update(userRef, {
                    taskCount: increment(-1)
                });
            }

            // Delete the task
            batch.delete(taskRef);

            // Commit the batch
            await batch.commit();

            return true;
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Task.collectionName);
    }

    static getById = async (id) => {
        return await getById(Task.collectionName, id);
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Task.collectionName, ids, 'in');
    }

    static getByUserId = async (userId) => {
        return await getBySingleQuery(Task.collectionName, 'userId', userId, '==');
    }

    static getByAssignedTo = async (userId) => {
        return await getBySingleQuery(Task.collectionName, 'assignedTo', userId, '==');
    }

    static getPending = async (userId) => {
        const list = await getBySingleQuery(Task.collectionName, 'assignedTo', userId, '==');
        return list.filter(task => ['PENDING', 'IN_PROGRESS'].includes(task.status));
    }

    static getOverdue = async () => {
        const now = new Date();
        const list = await Task.getAll();
        return list.filter(task => ['PENDING', 'IN_PROGRESS'].includes(task.status) && fromUtcToDate(task.dueAt, 'US/Pacific') < now);
    }

    static getByEntityIdAndEntityType = async (entityId, entityType) => {
        return await getByDoubleQuery(Task.collectionName, 'entityId', entityId, '==', 'entityType', entityType, '==');
    }

    static getByCaseKey = async (caseKey) => {
        if (isStringNotEmpty(caseKey)) {
            return await getBySingleQuery(Task.collectionName, 'caseKey', caseKey, '==');
        }

        return [];
    }

    static getByTicketNumber = async (ticketNumber) => {
        if (isStringNotEmpty(ticketNumber)) {
            return await getBySingleQuery(Task.collectionName, 'ticketNumber', ticketNumber, '==');
        }

        return [];
    }

    static getOpenByCaseKey = async (caseKey) => {
        const list = await Task.getByCaseKey(caseKey);
        return list.filter((task) => task?.type === 'TICKET' && ['PENDING', 'IN_PROGRESS'].includes(task?.status));
    }

    static getCount = async (queryParameters, dateFieldName = 'dueAt.timestamp') => {
        const { userId, assignedTo, status, priority, category, subCategory, channel, caseKey, customerResponseRequired, isSynced, isSuppressed, start, end, type } = queryParameters;

        let queryConstraints = [];

        if (isStringNotEmpty(type)) {
            queryConstraints.push(where('type', '==', type));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(priority)) {
            queryConstraints.push(where('priority', 'in', priority));
        } else if (isStringNotEmptyOrZeroLength(priority)) {
            queryConstraints.push(where('priority', '==', priority));
        }

        if (isListNotEmpty(category)) {
            queryConstraints.push(where('category', 'in', category));
        } else if (isStringNotEmptyOrZeroLength(category)) {
            queryConstraints.push(where('category', '==', category));
        }

        if (isListNotEmpty(subCategory)) {
            queryConstraints.push(where('subCategory', 'in', subCategory));
        } else if (isStringNotEmptyOrZeroLength(subCategory)) {
            queryConstraints.push(where('subCategory', '==', subCategory));
        }

        if (isListNotEmpty(assignedTo)) {
            queryConstraints.push(where('assignedTo', 'in', assignedTo));
        } else if (isStringNotEmptyOrZeroLength(assignedTo)) {
            queryConstraints.push(where('assignedTo', '==', assignedTo));
        }

        if (isListNotEmpty(channel)) {
            queryConstraints.push(where('channel', 'in', channel));
        } else if (isStringNotEmptyOrZeroLength(channel)) {
            queryConstraints.push(where('channel', '==', channel));
        }

        if (isStringNotEmptyOrZeroLength(caseKey)) {
            queryConstraints.push(where('caseKey', '==', caseKey));
        }

        if (isBooleanTrue(customerResponseRequired)) {
            queryConstraints.push(where('customerResponseRequired', '==', true));
        } else if (isBooleanFalse(customerResponseRequired)) {
            queryConstraints.push(where('customerResponseRequired', '==', false));
        }

        if (isBooleanTrue(isSynced)) {
            queryConstraints.push(where('isSynced', '==', true));
        } else if (isBooleanFalse(isSynced)) {
            queryConstraints.push(where('isSynced', '==', false));
        }

        if (isBooleanTrue(isSuppressed)) {
            queryConstraints.push(where('isSuppressed', '==', true));
        } else if (isBooleanFalse(isSuppressed)) {
            queryConstraints.push(where('isSuppressed', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '<=', endDate));
        }

        let q = query(collection(db, Task.collectionName), ...queryConstraints);
        const snapShot = await getCountFromServer(q);
        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'dueAt.timestamp', dateFieldName = 'dueAt.timestamp') => {
        const { userId, assignedTo, status, priority, category, subCategory, channel, caseKey, customerResponseRequired, isSynced, isSuppressed, start, end, type } = queryParameters;

        let queryConstraints = [];

        if (isStringNotEmpty(type)) {
            queryConstraints.push(where('type', '==', type));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(priority)) {
            queryConstraints.push(where('priority', 'in', priority));
        } else if (isStringNotEmptyOrZeroLength(priority)) {
            queryConstraints.push(where('priority', '==', priority));
        }

        if (isListNotEmpty(category)) {
            queryConstraints.push(where('category', 'in', category));
        } else if (isStringNotEmptyOrZeroLength(category)) {
            queryConstraints.push(where('category', '==', category));
        }

        if (isListNotEmpty(subCategory)) {
            queryConstraints.push(where('subCategory', 'in', subCategory));
        } else if (isStringNotEmptyOrZeroLength(subCategory)) {
            queryConstraints.push(where('subCategory', '==', subCategory));
        }

        if (isListNotEmpty(assignedTo)) {
            queryConstraints.push(where('assignedTo', 'in', assignedTo));
        } else if (isStringNotEmptyOrZeroLength(assignedTo)) {
            queryConstraints.push(where('assignedTo', '==', assignedTo));
        }

        if (isListNotEmpty(channel)) {
            queryConstraints.push(where('channel', 'in', channel));
        } else if (isStringNotEmptyOrZeroLength(channel)) {
            queryConstraints.push(where('channel', '==', channel));
        }

        if (isStringNotEmptyOrZeroLength(caseKey)) {
            queryConstraints.push(where('caseKey', '==', caseKey));
        }

        if (isBooleanTrue(customerResponseRequired)) {
            queryConstraints.push(where('customerResponseRequired', '==', true));
        } else if (isBooleanFalse(customerResponseRequired)) {
            queryConstraints.push(where('customerResponseRequired', '==', false));
        }

        if (isBooleanTrue(isSynced)) {
            queryConstraints.push(where('isSynced', '==', true));
        } else if (isBooleanFalse(isSynced)) {
            queryConstraints.push(where('isSynced', '==', false));
        }

        if (isBooleanTrue(isSuppressed)) {
            queryConstraints.push(where('isSuppressed', '==', true));
        } else if (isBooleanFalse(isSuppressed)) {
            queryConstraints.push(where('isSuppressed', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Task.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Task.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, Task.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters, dateFieldName = 'dueAt.timestamp') => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await this.getCount(queryParameters, dateFieldName);
            totalPages = Math.ceil(totalCount / 10000.0);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                let list = await this.getPaginated(queryParameters, 10000, null, next, dateFieldName, dateFieldName);
                allRecords = [...allRecords, ...list];
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
}

export default Task;
